import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, FileText, HelpCircle, BookOpen } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import AppShell from '@/components/AppShell';
import AssessmentDetail, { GRADE_META } from '@/components/AssessmentDetail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StateAssessment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialGrade = searchParams.get('grade') ? parseInt(searchParams.get('grade')) : null;
  const [selectedGrade, setSelectedGrade] = useState(initialGrade);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      const res = await axios.get(`${API}/assessments`);
      setAssessments(res.data);
    } catch {
      toast.error('Failed to load state assessments');
    } finally {
      setLoading(false);
    }
  };

  const grades = [7, 8, 9, 10, 11, 12];

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#5A6577]" />
        </div>
      </AppShell>
    );
  }

  const expanded = expandedId ? assessments.find(a => a.id === expandedId) : null;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Kansas HGSS &middot; Classroom-Based Assessment</p>
          <h1 className="font-serif-display text-4xl text-[#14213D] tracking-tight mb-3" data-testid="page-title">
            State Assessment Projects
          </h1>
          <p className="text-sm text-[#5A6577] max-w-2xl leading-relaxed">
            Inquiry-based assessments aligned with KSDE standards. Each assessment features a compelling question,
            primary sources, supporting questions across Social Studies disciplines, and the official KSDE rubric.
          </p>
        </div>

        {/* Grade Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="grade-filter">
          <Button
            variant={selectedGrade === null ? "default" : "outline"}
            size="sm"
            onClick={() => { setSelectedGrade(null); setExpandedId(null); }}
            className={selectedGrade === null ? 'bg-[#14213D]' : ''}
            data-testid="filter-all"
          >
            All Grades
          </Button>
          {grades.map(g => (
            <Button
              key={g}
              variant={selectedGrade === g ? "default" : "outline"}
              size="sm"
              onClick={() => { setSelectedGrade(g); setExpandedId(null); }}
              className={selectedGrade === g ? 'bg-[#14213D]' : ''}
              data-testid={`filter-grade-${g}`}
            >
              Grade {g}
            </Button>
          ))}
        </div>

        {/* Assessment Cards or Expanded Detail */}
        {expanded ? (
          <AssessmentDetail
            assessment={expanded}
            onBack={() => setExpandedId(null)}
          />
        ) : (
          <div className="space-y-10">
            {(selectedGrade ? [selectedGrade] : grades).map(grade => {
              const gradeAssessments = assessments
                .filter(a => a.grade_level === grade)
                .sort((a, b) => a.assessment_number - b.assessment_number);
              if (gradeAssessments.length === 0) return null;
              const meta = GRADE_META[grade] || {};
              const GradeIcon = meta.icon || BookOpen;
              return (
                <div key={grade} id={`grade-${grade}`} data-testid={`grade-section-${grade}`}>
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-[#EBE7E0]">
                    <div className="p-2 rounded-lg bg-[#F9F8F4]">
                      <GradeIcon className="h-5 w-5 text-[#14213D]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-serif text-[#14213D]">Grade {grade} — {meta.subject}</h3>
                      <p className="text-xs text-[#5A6577]">{meta.band} &middot; {gradeAssessments.length} assessment{gradeAssessments.length > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {gradeAssessments.map(a => (
                      <Card
                        key={a.id}
                        className="p-5 border border-[#EBE7E0] bg-white hover:shadow-md transition-shadow cursor-pointer group"
                        onClick={() => setExpandedId(a.id)}
                        data-testid={`assessment-card-${a.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold text-[#14213D] bg-[#FDFBF7] px-2 py-0.5 rounded">
                            #{a.assessment_number}
                          </span>
                          <span className="text-xs text-[#5A6577]">{a.unit_title}</span>
                        </div>
                        <h4 className="font-bold text-[#14213D] mb-2 group-hover:text-[#14213D] transition-colors text-sm">
                          {a.title}
                        </h4>
                        <p className="text-xs text-[#5A6577] italic leading-relaxed mb-3 line-clamp-2">
                          &ldquo;{a.compelling_question}&rdquo;
                        </p>
                        <div className="flex items-center gap-3 text-xs text-[#5A6577]">
                          <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {a.featured_sources?.length || 0} sources</span>
                          <span className="flex items-center gap-1"><HelpCircle className="h-3 w-3" /> {a.supporting_questions?.length || 0} Qs</span>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}

